import { api } from '@/api';
import { ActionContext } from 'vuex';
import { State } from '../state';
import { DocumentState } from './state';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetThirdPartyPayment, commitSetDocumentTemplates} from './mutations';
import { commitAddNotification } from '../main/mutations';
import { i18n } from '@/plugins/i18n';
import { IThirdPartyPaymentCreate, IInputModelSecondSeries, IInputModelReasoningSecondSession, IInputModelWrittenReport, IInputModelConsultativeResearch, IInputModelPathologyOverview, IDocumentTemplate } from '@/interfaces';

type MainContext = ActionContext<DocumentState, State>;

export const actions = {
  async actionGetThirdPartyPayment(context: MainContext, payload: IThirdPartyPaymentCreate) {
    try {
      const response = await api.createThirdPartyPayment(payload);
      console.log(response.headers);
      if (response) {
        const type = response.headers['content-type'];
        const blob = new Blob([response.data], { type});
        commitSetThirdPartyPayment(context, blob);
      }
    } catch (error: any) {
      console.log('error in actionGetThirdPartyPayment')
      console.log(error.response)
      console.log(error.response.data)
      const jsonMessage = JSON.parse(await error.response.data.text())
      if (error.response && error.response.status == 422){
        commitAddNotification(context, { color: 'error', content: jsonMessage.detail[0].msg });
      }
      else if (error.response){
        commitAddNotification(context, { color: 'error', content: jsonMessage.detail });
      }
    }
  },

  async createDocumentTemplate(context: MainContext, payload: {
    inputModel: IInputModelSecondSeries | IInputModelReasoningSecondSession | IInputModelWrittenReport | IInputModelConsultativeResearch | IInputModelPathologyOverview,
    patientId?: number,
    prescriptionId?: number,
    exportFormat?: 'pdf' | 'docx',
    locale?: string,
  }) {
    try {
      const response = await api.createSecondSeriesTemplate(
        payload.inputModel,
        payload.patientId,
        payload.prescriptionId,
        payload.exportFormat,
        payload.locale
      );

      if (response) {
        // Add the new document template to the list
        const currentTemplates = context.state.document_templates || [];
        commitSetDocumentTemplates(context, [response.data, ...currentTemplates]);

        commitAddNotification(context, {
          color: 'success',
          content: String(i18n.t('documents.created'))
        });
      }
    } catch (error: any) {
      console.log('error in createDocumentTemplate');
      console.log(error.response);
      let errorMessage = String(i18n.t('documents.createError'));

      if (error.response && error.response.data) {
        try {
          const jsonMessage = JSON.parse(await error.response.data.text());
          if (error.response.status === 422) {
            errorMessage = jsonMessage.detail[0].msg;
          } else {
            errorMessage = jsonMessage.detail;
          }
        } catch (parseError) {
          errorMessage = String(i18n.t('documents.createError'));
        }
      }

      commitAddNotification(context, { color: 'error', content: errorMessage });
    }
  },

  async getDocumentTemplates(context: MainContext, payload: { skip?: number, limit?: number } = {}) {
    try {
      console.log('Fetching document templates...');
      let skip = 0;
      const limit = 100;
      const results: IDocumentTemplate[] = [];
      while (true){
        const response = await api.getDocumentTemplates(
          payload.skip || skip,
          payload.limit || limit
        );
        if (response) {
          if (response.data.length == 0){
            break
          }
          results.push(...response.data)
          skip += limit;
        }
      }
      console.log('Document templates response:', results);
      if (results) {
        commitSetDocumentTemplates(context, results);
        console.log('Document templates committed to store');
      }
    } catch (error: any) {
      console.log('error in getDocumentTemplates');
      console.log(error.response);
      let errorMessage = String(i18n.t('documents.loadTemplatesError'));

      if (error.response && error.response.data) {
        try {
          const jsonMessage = JSON.parse(await error.response.data.text());
          errorMessage = jsonMessage.detail;
        } catch (parseError) {
          errorMessage = String(i18n.t('documents.loadTemplatesError'));
        }
      }

      commitAddNotification(context, { color: 'error', content: errorMessage });
    }
  },
};

const { dispatch } = getStoreAccessors<DocumentState, State>('');

export const dispatchGetThirdPartyPayment = dispatch(actions.actionGetThirdPartyPayment);
export const dispatchCreateDocumentTemplate = dispatch(actions.createDocumentTemplate);
export const dispatchGetDocumentTemplates = dispatch(actions.getDocumentTemplates);
